import { useMemo, useEffect, useCallback } from "react";
import { calcParkingBreakdown } from "../../../HELPERS/parkingStays"; // adjust path to match your tree

const useBillingCalculation = ({ rooms, values, taxConfig, setValues }) => {
    const percentOf = useCallback(
        (percent, base) => (Number(percent || 0) * Number(base || 0)) / 100,
        []
    );

    const diffInDays = useCallback((from, to) => {
        if (!from || !to) return 1;
        const diff = Math.ceil((new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24));
        return diff > 0 ? diff : 1;
    }, []);

    const computedRooms = useMemo(() => {
        return rooms.map((room) => {
            const facilityFrom = values.checkin_date;
            const ExtraFacilityFrom = room.extracheck_in_date || values.checkin_date

            const facilityTo = values.checkout_date;
            const ExtraFacilityTo = room.extracheck_out_date || values.checkout_date

            const facilityDays = diffInDays(facilityFrom, facilityTo);
            const ExtraFacilityDays = diffInDays(ExtraFacilityFrom, ExtraFacilityTo)

            const extra_bed_charge = Number(room.extra_bed || 0) * Number(room.extra_bed_charge || 0) * ExtraFacilityDays;
            const extra_person_charge = Number(room.extra_person || 0) * Number(room.extra_person_charge || 0) * ExtraFacilityDays;
            const extra_child_charge = Number(room.extra_child || 0) * Number(room.extra_child_charge || 0) * ExtraFacilityDays;

            const extra_room_total = extra_bed_charge + extra_person_charge + extra_child_charge;

            const room_rate = Number(room.room_rate || 0);
            const room_rate_total = room_rate * facilityDays;

            const complementary_price = Number(room.complementary_price || 0);
            const complementary_price_type = room.complementary_price_type || "";


            const multiplier =
                room.accommodation_type === "DORMITORY"
                    ? Number(room.no_of_bed || 0)
                    : 1;

            const complementary_total =
                complementary_price_type === "DAY_WISE"
                    ? complementary_price * facilityDays * multiplier
                    : complementary_price * multiplier;


            const bed_rate_total =
                Number(room.rate_per_bed || 0) * Number(room.no_of_bed || 0) * facilityDays +
                complementary_total;

            const subtotal = room_rate_total + extra_room_total + complementary_total;

            const room_total =
                (room_rate_total) + (extra_room_total + complementary_total);

            const final_total =
                room.accommodation_type === "DORMITORY" ? bed_rate_total : room_total;

            return {
                ...room,
                room_rate,
                room_rate_total,
                complementary_total,
                complementary_price,
                complementary_price_type,
                multiplier,
                facilityDays,
                extra_bed_charge,
                extra_person_charge,
                extra_child_charge,
                extra_room_total,
                subtotal,
                room_total,
                bed_rate_total,
                final_total,
            };
        });
    }, [rooms, values.checkin_date, values.checkout_date, diffInDays]);

    const billingDeps = useMemo(
        () => ({
            roomsSum: computedRooms.reduce((sum, r) => sum + (Number(r.final_total) || 0), 0),
            extrasSum: computedRooms.reduce((sum, r) => sum + (Number(r.extra_room_total) || 0), 0),
        }),
        [computedRooms]
    );

    const billingRates = useMemo(() => {
        if (rooms.length === 0) {
            return {
                baseRent: 0,
                extras: 0,
                tax: 0,
                total_tax_rate: 0,
                serviceCharge: 0,
                service_charge: 0,
                total: 0,
                RoomBookingCharge: 0,
                AdvanceAmount: 0,
                PrevBalance: 0,
                RemainingAmount: 0,
                parking_price: 0,
                parking_price_total: 0,
                facilityDays: 1,
            };
        }

        const baseRent = billingDeps.roomsSum;

        const service_charge = Number(taxConfig?.service_charge ?? 0);

        const facilityDays = diffInDays(values.checkin_date, values.checkout_date);

        // ---- PARKING: derived from movement log, not a flat formula ----
        const parking_price = Number(taxConfig?.parking_price ?? 0);
        const { total: parking_price_total } = calcParkingBreakdown(
            values.parking_movements,
            parking_price,
            values.checkin_date,
            values.checkout_date
        );

        const serviceCharge = percentOf(service_charge, baseRent);

        const taxableAmount = baseRent + serviceCharge + parking_price_total;
        let allTaxes = taxConfig?.all_taxs ?? [];

        try {
            while (typeof allTaxes === "string") {
                allTaxes = JSON.parse(allTaxes);
            }

            if (!Array.isArray(allTaxes)) {
                allTaxes = [];
            }
        } catch (error) {
            allTaxes = [];
        }

        const taxBreakdown = allTaxes.map((item) => ({
            tax_id: item.tax_id,
            tax_name: item.tax_name,
            rate: Number(item.rate) || 0,
            amount: percentOf(Number(item.rate) || 0, taxableAmount),
        }));

        const tax = taxBreakdown.reduce(
            (sum, item) => sum + item.amount,
            0
        );

        const total_tax_rate = taxBreakdown.reduce(
            (sum, item) => sum + item.rate,
            0
        );

        const total = Math.round(baseRent + serviceCharge + parking_price_total + tax);
        const RoomBookingCharge = baseRent;

        const AdvanceAmount = (values.PaymentDetail || []).reduce(
            (sum, p) => sum + Number(p.advance_amount || 0),
            0
        );

        const PrevBalance = Number(values.avalible_balance || 0);

        const applyBalance = values.is_apply_balance === true || values.is_apply_balance === "true";

        const is_prev_apply_balance = values?.is_prev_apply_balance;
        let prev_used_balance = ""
        if (is_prev_apply_balance) {
            prev_used_balance = Number(values?.prev_used_balance) || 0
        }

        const currentDue = Number(total || 0) - Number(AdvanceAmount || 0);
        const dueAfterPrevUsed = currentDue - Number(prev_used_balance || 0);

        const availableCredit = applyBalance && PrevBalance > 0 ? PrevBalance : 0;
        const outstandingDue = applyBalance && PrevBalance < 0 ? Math.abs(PrevBalance) : 0;

        const creditApplied = Math.min(availableCredit, Math.max(dueAfterPrevUsed, 0));

        const RemainingAmount = (dueAfterPrevUsed - creditApplied) + outstandingDue;

        const is_apply_balance_amount = applyBalance ? creditApplied : 0;

        return {
            baseRent,
            extras: billingDeps.extrasSum,
            tax,
            taxBreakdown,
            total_tax_rate,
            serviceCharge,
            service_charge,
            total,
            RoomBookingCharge,
            AdvanceAmount,
            PrevBalance,
            RemainingAmount,
            parking_price,
            parking_price_total,
            facilityDays,
            is_apply_balance_amount,
            is_prev_apply_balance,
            prev_used_balance
        };
    }, [
        billingDeps,
        taxConfig,
        percentOf,
        diffInDays,
        rooms.length,
        values.PaymentDetail,
        values.parking_movements,
        values.checkin_date,
        values.checkout_date,
        values.avalible_balance,
        values.is_apply_balance,
        values.is_prev_apply_balance,   // add
        values.prev_used_balance,        // add
    ]);

    useEffect(() => {
        if (!rooms.length) return;

        setValues((prev) => {
            const next = { ...prev };
            let changed = false;

            const newTotal = String(billingRates.total);
            if (prev.total_amount !== newTotal) {
                next.total_amount = newTotal;
                changed = true;
            }

            if (prev.service_charge !== billingRates.service_charge) {
                next.service_charge = billingRates.service_charge;
                changed = true;
            }

            if (JSON.stringify(prev.tax_details) !== JSON.stringify(billingRates.taxBreakdown)) {
                next.tax_details = billingRates.taxBreakdown;
                changed = true;
            }

            if (prev.parking_rate !== billingRates.parking_price) {
                next.parking_rate = billingRates.parking_price;
                changed = true;
            }

            if (prev.advance_amount !== billingRates.AdvanceAmount) {
                next.advance_amount = billingRates.AdvanceAmount;
                changed = true;
            }

            if (prev.is_apply_balance_amount !== billingRates.is_apply_balance_amount) {
                next.is_apply_balance_amount = billingRates.is_apply_balance_amount;
                changed = true;
            }

            if (prev.parking_price_total !== billingRates.parking_price_total) {
                next.parking_price_total = billingRates.parking_price_total;
                changed = true;
            }

            const nextRooms = prev.rooms.map((room, idx) => {
                const computed = computedRooms[idx];
                if (!computed) return room;

                const newRoomTotal = computed.final_total;
                if (room.room_total !== newRoomTotal) {
                    changed = true;
                    return { ...room, room_total: newRoomTotal };
                }
                return room;
            });

            if (changed) {
                next.rooms = nextRooms;
            }

            return changed ? next : prev;
        });
    }, [billingRates, rooms.length, setValues, computedRooms]);

    return { billingRates, computedRooms };
};

export default useBillingCalculation;